import {
  Autocomplete,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import PatientsAutoComplete from 'components/autocomplete/PatientsAutoComplete';
import ProceduresAutoComplete from 'components/autocomplete/ProceduresAutoComplete';
import FormField from 'components/form-page/FormField';
import FormSection from 'components/form-page/FormSection';
import MoneyInput from 'components/masked-input/MoneyInput';
import { usePermissionRules } from 'hooks/permissionRules';
import { Doctor } from 'types/doctor';
import { GuidePaymentMethod, GuideTypeForm } from 'types/guide';
import { PaymentMethod } from 'types/paymentMethod';
import { User } from 'types/user';
import { GuideValidation } from '../validation/useGuideValidation';
import { asInputComponent } from 'components/masked-input/asInputComponent';

const ListItemStyled = styled('li')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  justifyContent: 'flex-start',
  padding: '8px 16px',
  cursor: 'pointer',
  '& svg': {
    color: '#ccc',
  },
  '&[aria-disabled="true"]': {
    opacity: 0.5,
    pointerEvents: 'none',
  },
});

const FieldGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  alignItems: 'start',
  '& .full': {
    gridColumn: '1 / -1',
  },
  '&.money': {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  },
  '&.payment': {
    gridTemplateColumns: 'auto repeat(2, minmax(0, 1fr))',
    alignItems: 'end',
  },
  '& .bonus': {
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  [theme.breakpoints.down('md')]: {
    '&.money': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    '&.money, &.payment': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
}));

const moneyField = () => ({
  input: {
    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
    inputComponent: asInputComponent(MoneyInput),
  },
  htmlInput: { style: { textAlign: 'right' as const } },
});

interface GuideFormProps {
  guide: GuideTypeForm;
  paymentMethod: GuidePaymentMethod;
  paymentMethods: PaymentMethod[];
  loadingPaymentMethods: boolean;
  handleChange: <K extends keyof GuideTypeForm>(index: K, value: GuideTypeForm[K]) => void;
  validation: GuideValidation;
  handleChangePayment: <K extends keyof GuidePaymentMethod>(index: K, value: GuidePaymentMethod[K]) => void;
  users: User[];
  loadingUsers: boolean;
}

const GuideForm: React.FC<GuideFormProps> = ({
  guide,
  handleChange,
  users,
  loadingUsers,
  handleChangePayment,
  validation,
  paymentMethod,
  paymentMethods,
  loadingPaymentMethods,
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const { checkPermission } = usePermissionRules();

  useEffect(() => {
    setSelectedUser(users.find(u => u.id === guide.marker_id) || null);
  }, [guide.marker_id, users]);

  function onSelect(user: User | null) {
    setSelectedUser(user);
    handleChange('marker_id', user ? user.id : null);
  }

  const inputs = {
    marker_id: useRef<HTMLInputElement>(null),
    value_marker: useRef<HTMLInputElement>(null),
    value_procedure: useRef<HTMLInputElement>(null),
    value_discount: useRef<HTMLInputElement>(null),
    payment_method_id: useRef<HTMLInputElement>(null),
    patient_id: useRef<HTMLInputElement>(null),
    procedure_id: useRef<HTMLInputElement>(null),
    doctor_id: useRef<HTMLInputElement>(null),
    appointment_date: useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    const validationOrder: Array<keyof typeof inputs> = [
      'patient_id',
      'appointment_date',
      'procedure_id',
      'doctor_id',
      'marker_id',
      'payment_method_id',
    ];
    const key = validationOrder.find(item => validation[item]);

    if (!key) return;

    setTimeout(() => {
      const input = inputs[key].current;
      const scrollTarget = input?.closest?.('.MuiFormControl-root') || input;

      input?.focus?.();
      if (scrollTarget && 'scrollIntoView' in scrollTarget) {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }, [validation]);

  return (
    <>
      <FormSection number={1} title="Paciente">
        <FormField label="Informe o paciente">
          <PatientsAutoComplete
            ref={inputs.patient_id}
            label=""
            hideHint
            popupIcon={<Search fontSize="small" />}
            onSelect={patient => {
              handleChange('patient', patient);
              handleChange('patient_id', patient?.id ?? null);
            }}
            placeholder="Digite 2 ou mais caracteres para buscar..."
            selected={guide.patient}
            error={!!validation.patient_id}
            helperText={validation.patient_id}
          />
        </FormField>
      </FormSection>

      <FormSection number={2} title="Consulta">
        <FieldGrid>
          <FormField label="Data da consulta">
            <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
              <DateTimePicker
                disablePast
                value={guide.appointment_date}
                onChange={date => handleChange('appointment_date', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    inputRef: inputs.appointment_date,
                    helperText: validation.appointment_date,
                    error: !!validation.appointment_date,
                  },
                }}
              />
            </LocalizationProvider>
          </FormField>

          <FormField label="Informe a consulta">
            <ProceduresAutoComplete
              type="G"
              ref={inputs.procedure_id}
              label=""
              hideHint
              popupIcon={<Search fontSize="small" />}
              onSelect={procedure => {
                const value_procedure = procedure?.sum_commission_to_total
                  ? procedure.sale_price + procedure.commission
                  : procedure?.sale_price;
                handleChange('procedure', procedure);
                handleChange('procedure_id', procedure?.id ?? null);
                handleChange('value_marker', procedure?.commission ?? 0);
                handleChange('value_procedure', value_procedure ?? 0);
                setDoctors(
                  procedure?.doctors.map(item => item.doctor).filter((doctor): doctor is Doctor => doctor !== null) ||
                    [],
                );
              }}
              placeholder="Buscar consulta..."
              selected={guide.procedure}
              error={!!validation.procedure_id}
              helperText={validation.procedure_id}
            />
          </FormField>

          <div className="full">
            <FormField label="Parceiro">
              <Autocomplete
                fullWidth
                autoHighlight
                handleHomeEndKeys
                openOnFocus
                value={guide.doctor}
                options={doctors}
                noOptionsText="Nenhum parceiro encontrado"
                getOptionLabel={option => option?.name || ''}
                onChange={(event, newValue) => {
                  handleChange('doctor', newValue);
                  handleChange('doctor_id', newValue ? newValue.id : null);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Selecione um parceiro"
                    inputRef={inputs.doctor_id}
                    error={!!validation.doctor_id}
                    helperText={validation.doctor_id}
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <ListItemStyled {...props} key={option.id} aria-disabled={guide.doctor_id === option.id}>
                    <div>
                      <Typography>{option.name}</Typography>
                    </div>
                  </ListItemStyled>
                )}
              />
            </FormField>
          </div>
        </FieldGrid>
      </FormSection>

      <FormSection number={3} title="Pagamento">
        <FieldGrid className="money">
          <FormField label="Custo da consulta">
            <TextField
              fullWidth
              disabled
              value={guide.procedure ? guide.procedure.cost : ''}
              slotProps={moneyField()}
            />
          </FormField>

          <FormField label="Valor do marcador">
            <TextField
              fullWidth
              value={guide.value_marker}
              onChange={e => handleChange('value_marker', Number(e.target.value))}
              disabled={!guide.marker_id}
              error={!!validation.value_marker}
              helperText={
                validation.value_marker ||
                (guide.procedure?.sum_commission_to_total ? 'Incluído no valor total' : 'Não incluído no total')
              }
              inputRef={inputs.value_marker}
              slotProps={moneyField()}
            />
          </FormField>

          <FormField label="Valor da consulta">
            <TextField
              fullWidth
              disabled={!guide.procedure_id}
              inputRef={inputs.value_procedure}
              value={guide.value_procedure}
              onChange={e => handleChange('value_procedure', Number(e.target.value))}
              error={!!validation.value_procedure}
              helperText={validation.value_procedure}
              slotProps={moneyField()}
            />
          </FormField>

          <FormField label="Desconto">
            <Tooltip
              title={
                checkPermission('ambulatory.guide.new.give-discount') ? '' : 'Você não tem permissão para dar desconto'
              }
            >
              <TextField
                fullWidth
                error={!!validation.value_discount}
                helperText={validation.value_discount}
                disabled={!guide.procedure_id || !checkPermission('ambulatory.guide.new.give-discount')}
                value={guide.value_discount}
                onChange={e => handleChange('value_discount', Number(e.target.value))}
                inputRef={inputs.value_discount}
                slotProps={moneyField()}
              />
            </Tooltip>
          </FormField>
        </FieldGrid>

        <Divider />

        <FieldGrid className="payment">
          <FormControlLabel
            className="bonus"
            label={<Typography variant="body2">Bonificar consulta</Typography>}
            control={
              <Checkbox
                size="small"
                checked={guide.value_procedure > 0 && guide.value_procedure === guide.value_discount}
                onChange={e => {
                  handleChange('value_discount', e.target.checked ? guide.value_procedure : 0);
                }}
              />
            }
          />

          <FormField label="Marcador">
            <Autocomplete
              fullWidth
              autoHighlight
              handleHomeEndKeys
              openOnFocus
              disabled={loadingUsers}
              value={selectedUser}
              options={users}
              noOptionsText="Nenhum marcador encontrado"
              getOptionLabel={option => option.name}
              onChange={(event, newValue) => onSelect(newValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  disabled={loadingUsers}
                  placeholder="Selecione o marcador"
                  inputRef={inputs.marker_id}
                  error={!!validation.marker_id}
                  helperText={validation.marker_id}
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <ListItemStyled {...props} key={option.id} aria-disabled={guide.marker_id === option.id}>
                  <div>
                    <Typography>{option.name}</Typography>
                  </div>
                </ListItemStyled>
              )}
            />
          </FormField>

          <FormField label="Método de pagamento">
            <TextField
              select
              disabled={loadingPaymentMethods}
              value={paymentMethod.payment_method_id}
              onChange={e => handleChangePayment('payment_method_id', Number(e.target.value))}
              inputRef={inputs.payment_method_id}
              error={!!validation.payment_method_id}
              helperText={validation.payment_method_id}
              fullWidth
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected: unknown) => {
                    const method = paymentMethods.find(item => item.id === selected);
                    return method ? method.name : <span style={{ opacity: 0.6 }}>Selecione o método</span>;
                  },
                },
              }}
            >
              {paymentMethods.map(method => (
                <MenuItem key={method.id} value={method.id}>
                  {method.name}
                </MenuItem>
              ))}
            </TextField>
          </FormField>
        </FieldGrid>
      </FormSection>

      <FormSection number={4} title="Observações">
        <TextField
          fullWidth
          placeholder="Detalhes adicionais..."
          value={guide.observation || ''}
          onChange={e => handleChange('observation', e.target.value)}
          slotProps={{
            // textarea simples (sem autosize do MUI) com altura fixa de 4 linhas
            input: { inputComponent: 'textarea' },
            htmlInput: { rows: 4, style: { height: 'auto', resize: 'vertical' } },
          }}
        />
      </FormSection>
    </>
  );
};

export default GuideForm;
